import { useRef, useState } from 'react'
import { Link, createFileRoute, redirect, useBlocker, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import {
  getActivationsAdminStudentProjectsListQueryKey,
  getV1MeRetrieveQueryOptions,
  useActivationsAdminStudentProjectsCreate,
} from '@/api/client'
import type { AdminStudentProjectWriteRequest } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { ActivationForm } from '@/components/activation/activation-form'
import type { ActivationWriteRequest } from '@/components/activation/activation-form-schema'

export const Route = createFileRoute('/_panel/zadania/projekty/nowe')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: StudentProjectCreatePage,
})

function StudentProjectCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const createMutation = useActivationsAdminStudentProjectsCreate()
  const [dirty, setDirty] = useState(false)
  const allowSuccessfulNavigation = useRef(false)
  useBlocker({
    shouldBlockFn: () =>
      dirty &&
      !allowSuccessfulNavigation.current &&
      !window.confirm('Odrzucić niezapisane zmiany szkicu?'),
    enableBeforeUnload: () => dirty,
  })

  const handleSubmit = async (payload: ActivationWriteRequest) => {
    const response = await createMutation.mutateAsync({
      data: payload as AdminStudentProjectWriteRequest,
    })
    if (response.status === 201) {
      await queryClient.invalidateQueries({
        queryKey: getActivationsAdminStudentProjectsListQueryKey(),
      })
      allowSuccessfulNavigation.current = true
      setDirty(false)
      try {
        await navigate({ to: '/zadania/projekty/$uid', params: { uid: response.data.uid } })
      } finally {
        allowSuccessfulNavigation.current = false
      }
    }
    return response
  }

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon-sm">
              <Link to="/zadania/projekty">
                <ArrowLeft />
              </Link>
            </Button>
            Nowy projekt uczniowski
          </span>
        }
      />
      <div className="p-6">
        <ActivationForm
          mode="create"
          submitLabel="Zapisz szkic"
          onCancel={() => {
            setDirty(false)
            void navigate({ to: '/zadania/projekty' })
          }}
          onSubmit={handleSubmit}
          onDirtyChange={setDirty}
        />
      </div>
    </>
  )
}
